import { useState } from "react";
import { Link } from "react-router-dom";
import { Send, Clock, MapPin, Mail } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLine } from "@/components/Reveal";
import GmtClock from "@/components/GmtClock";
import api, { formatApiError } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", target: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Enquiry received — we reply within one UK working day.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="mx-auto max-w-7xl px-5 pb-24 pt-20 lg:px-8 lg:pt-28">
        <span className="overline-label">Contact</span>
        <h1 className="mt-5 max-w-3xl font-serif text-4xl font-semibold leading-tight text-slate-50 sm:text-5xl lg:text-6xl" data-testid="contact-headline">
          <MaskedLine delay={0.1}>Let's talk about</MaskedLine>
          <MaskedLine delay={0.3} className="italic text-[#C5A059]">your UK move.</MaskedLine>
        </h1>

        <div className="mt-16 grid gap-12 lg:grid-cols-5">
          {/* FORM */}
          <Reveal className="lg:col-span-3">
            <div className="card-surface p-8 lg:p-10">
              {sent ? (
                <div className="py-10 text-center" data-testid="contact-success">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15">
                    <Send size={22} className="text-emerald-400" />
                  </div>
                  <h2 className="mt-6 font-serif text-2xl font-semibold text-slate-50">Enquiry received</h2>
                  <p className="mx-auto mt-3 max-w-sm text-sm text-slate-400">
                    Thank you, {form.name.split(" ")[0]}. We respond to every enquiry within one UK working day (GMT hours).
                  </p>
                  <Link to="/services" className="btn-ghost mt-8" data-testid="contact-success-services-btn">Browse Services</Link>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400" htmlFor="contact-name">Full Name *</label>
                      <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className="input-dark" placeholder="Your full name" />
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400" htmlFor="contact-email">Email *</label>
                      <input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} className="input-dark" placeholder="you@email.com" />
                    </div>
                  </div>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400" htmlFor="contact-phone">Phone</label>
                      <input id="contact-phone" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} className="input-dark" placeholder="+44 ... / +91 ..." />
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400" htmlFor="contact-target">Target UK City / Industry</label>
                      <input id="contact-target" data-testid="contact-target-input" value={form.target} onChange={set("target")} className="input-dark" placeholder="e.g. MSc student — London, FinTech" />
                    </div>
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400" htmlFor="contact-message">Your Enquiry *</label>
                    <textarea id="contact-message" data-testid="contact-message-input" required rows={6} value={form.message} onChange={set("message")} className="input-dark resize-none" placeholder="Tell us about your situation — your course and university, visa status (Student / Graduate / sponsorship needed), target roles and timeline." />
                  </div>
                  <button type="submit" disabled={sending} className="btn-gold disabled:opacity-60" data-testid="contact-form-submit-btn">
                    {sending ? "Sending…" : "Send Enquiry"} <Send size={15} />
                  </button>
                </form>
              )}
            </div>
          </Reveal>

          {/* INFO PANEL */}
          <Reveal delay={0.15} className="lg:col-span-2">
            <div className="space-y-6">
              <div className="card-surface gold-stroke p-8" data-testid="hours-widget">
                <div className="flex items-center gap-3">
                  <Clock size={20} className="text-[#C5A059]" />
                  <h3 className="font-serif text-xl font-semibold text-slate-50">Operating Hours</h3>
                </div>
                <div className="mt-5 space-y-3 text-sm text-slate-300">
                  <div className="flex justify-between border-b border-slate-700/50 pb-3"><span>Monday – Saturday</span><span className="font-mono text-slate-100">09:00 – 18:00 GMT/BST</span></div>
                  <div className="flex justify-between"><span>Sunday</span><span className="font-mono text-slate-400">Closed</span></div>
                </div>
                <div className="mt-6 rounded-lg bg-[#0A0F1D] px-4 py-3">
                  <GmtClock showStatus />
                </div>
                <p className="mt-4 text-xs leading-relaxed text-slate-500">
                  Our Sheffield desk is staffed to UK business hours (GMT/BST), with our Hyderabad branch team keeping turnaround times fast.
                </p>
              </div>

              <div className="card-surface p-8" data-testid="contact-details-card">
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <Mail size={18} className="mt-1 shrink-0 text-[#C5A059]" />
                    <div>
                      <div className="text-sm font-semibold text-slate-50">Email</div>
                      <div className="text-sm text-slate-400">hello.rmsgroup@gmail.com</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <MapPin size={18} className="mt-1 shrink-0 text-[#C5A059]" />
                    <div>
                      <div className="text-sm font-semibold text-slate-50">Offices</div>
                      <div className="text-sm text-slate-400">Sheffield, United Kingdom (HQ) · Branch: Hyderabad, India</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-emerald-800/50 bg-emerald-950/30 p-6 text-sm leading-relaxed text-emerald-100/80" data-testid="response-promise">
                Custom corporate quotes and bulk team orders welcome — mention your team size in the enquiry and we'll respond with tailored GBP pricing.
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
